import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Stethoscope, X } from '@phosphor-icons/react';
import { useMaskedDoctor } from '@/components/DoctorBits';
import DoctorForm from '@/components/DoctorForm';

/** Picks a doctor from the directory. value = { provider_id, name } | null. */
export default function DoctorPicker({ patientId, value, onChange, testid = 'doctor-picker', placeholder = 'Choose a doctor…', allowClear = true, excludeIds = [] }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [creating, setCreating] = useState(false);
    const maskDoctor = useMaskedDoctor();
    const { data: doctors = [] } = useQuery({
        queryKey: ['doctors', patientId || 'all'],
        queryFn: async () => (await api.get('/doctors', { params: patientId ? { patient_id: patientId } : {} })).data,
        enabled: open,
    });
    const needle = q.trim().toLowerCase();
    const filtered = doctors.filter(d => !excludeIds.includes(d.provider_id) &&
        (!needle || (d.name || '').toLowerCase().includes(needle) || (d.specialty || '').toLowerCase().includes(needle)));
    const linked = filtered.filter(d => d.linked);
    const others = filtered.filter(d => !d.linked);
    const pick = (d) => { onChange({ provider_id: d.provider_id, name: d.name }); setOpen(false); setQ(''); };
    const close = (v) => { setOpen(v); if (!v) { setCreating(false); setQ(''); } };

    const Row = ({ d }) => (
        <button type="button" data-testid={`${testid}-option`} onClick={() => pick(d)}
            className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-slate-50 flex items-center gap-3">
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{maskDoctor(d.name)}</div>
                <div className="text-[11px] text-slate-500">{d.specialty || 'Specialty not set'}{d.link_role ? ` · ${d.link_role}` : ''}</div>
            </div>
        </button>
    );

    return (
        <div className="flex items-center gap-2">
            <Button type="button" data-testid={testid} variant="outline" className="flex-1 justify-start min-h-[44px] sm:min-h-10 font-normal" onClick={() => setOpen(true)}>
                <Stethoscope size={16} className="mr-2 text-slate-500 shrink-0" />
                <span className={`truncate ${value?.name ? 'text-slate-900' : 'text-slate-500'}`}>{value?.name ? maskDoctor(value.name) : placeholder}</span>
            </Button>
            {allowClear && value?.name && (
                <button type="button" data-testid={`${testid}-clear`} aria-label="Clear doctor" onClick={() => onChange(null)}
                    className="min-h-[44px] min-w-[44px] sm:min-h-10 sm:min-w-10 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
                    <X size={16} />
                </button>
            )}
            <Dialog open={open} onOpenChange={close}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{creating ? 'Add a new doctor' : 'Pick a doctor'}</DialogTitle><DialogDescription>{creating ? 'New doctors also join your doctors list.' : 'Search your doctors list, or add a new one.'}</DialogDescription></DialogHeader>
                    {!creating ? (
                        <>
                            <Input data-testid={`${testid}-search`} autoFocus placeholder="Search by name or specialty" value={q} onChange={e => setQ(e.target.value)} />
                            <div className="max-h-72 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                                {linked.length > 0 && (
                                    <>
                                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-50">Their doctors</div>
                                        {linked.map(d => <Row key={d.provider_id} d={d} />)}
                                    </>
                                )}
                                {others.length > 0 && (
                                    <>
                                        {linked.length > 0 && <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-50">All doctors</div>}
                                        {others.map(d => <Row key={d.provider_id} d={d} />)}
                                    </>
                                )}
                                {filtered.length === 0 && <div className="px-3 py-6 text-sm text-slate-500 text-center">No doctors match.</div>}
                            </div>
                            <Button type="button" data-testid={`${testid}-add-new`} variant="outline" className="w-full min-h-[44px]" onClick={() => setCreating(true)}>
                                <Plus size={14} className="mr-1" /> Add a new doctor
                            </Button>
                        </>
                    ) : (
                        <DoctorForm
                            onCancel={() => setCreating(false)}
                            onSaved={(d) => { qc.invalidateQueries({ queryKey: ['doctors'] }); pick(d); setCreating(false); }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
